import moment from 'moment-timezone';
import { TIME_ZONES } from '../../../../../helper/constants';
import { momentDayToApiDayOfWeek } from '../../../../bookings/V2/bookingFormHelpers';

export function isSharedAmenity(buildingIds) {
  return Array.isArray(buildingIds) && buildingIds.length > 0;
}

function normalizeBuildingId(id) {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

function collectBuildingIds(primaryId, extraIds) {
  const ids = new Set();
  const primary = normalizeBuildingId(primaryId);
  if (primary != null) ids.add(primary);

  if (Array.isArray(extraIds)) {
    for (const rawId of extraIds) {
      const id = normalizeBuildingId(rawId);
      if (id != null) ids.add(id);
    }
  }

  return ids;
}

export function canManagerEditAmenity(amenity, manager) {
  const amenityIds = collectBuildingIds(amenity?.building_id, amenity?.building_ids);
  if (amenityIds.size === 0) return true;

  const managerIds = collectBuildingIds(manager?.building_id, manager?.assigned_buildings);
  for (const id of amenityIds) {
    if (!managerIds.has(id)) return false;
  }
  return true;
}

export function formatAmenityBuildingsDisplay(amenity) {
  if (!amenity) return '—';

  const primary = amenity?.building_name ?? amenity?.building ?? '';
  const sharedNames = Array.isArray(amenity?.building_names)
    ? amenity.building_names
        .map((name) => (name != null ? String(name).trim() : ''))
        .filter(Boolean)
    : [];

  const parts = [primary, ...sharedNames].map((name) => String(name).trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function formatAmenityBuildingsSummaryFromCreate(primaryBuildingName, buildingIds) {
  const primary =
    primaryBuildingName != null ? String(primaryBuildingName).trim() : '';
  const sharedNames = Array.isArray(buildingIds)
    ? buildingIds
        .map((entry) => (entry?.label != null ? String(entry.label).trim() : ''))
        .filter(Boolean)
    : [];
  const parts = [primary, ...sharedNames].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function formatAvailabilityDisplayLines(availabilityDisplay) {
  if (!Array.isArray(availabilityDisplay) || availabilityDisplay.length === 0) {
    return null;
  }

  const lines = availabilityDisplay
    .map((entry) => {
      const days = entry?.days != null ? String(entry.days).trim() : '';
      const time = entry?.time != null ? String(entry.time).trim() : '';
      if (days && time) {
        return `${days} ${time}`;
      }
      return days || time || '';
    })
    .filter(Boolean);

  return lines.length > 0 ? lines : null;
}

export function formatAvailabilityDisplayForCsv(lines) {
  if (!lines || lines.length === 0) {
    return '—';
  }
  return lines.join('\n');
}

export function getAmenityBookingTypeLabel(allowBooking, t) {
  return allowBooking ? t('Reserve') : t('Drop in');
}

export function getManagerApprovalLabel(requireManagerApproval, t) {
  return requireManagerApproval ? t('Yes') : t('No');
}

export function resolveBuildingTimezoneByState(state) {
  if (state == null || state === '') {
    return null;
  }
  return TIME_ZONES.get(String(state).trim().toUpperCase()) ?? null;
}

export function resolveViewingBuildingTimezone(viewingBuilding) {
  return resolveBuildingTimezoneByState(viewingBuilding?.state);
}

function findScheduleForDay(schedules, dayOfWeek) {
  return schedules.find((s) => Number(s.day_of_week) === dayOfWeek);
}

function buildScheduleWindow(scheduleRow, baseDay, timezone) {
  if (!scheduleRow || scheduleRow.is_closed) {
    return null;
  }

  const startUtc = moment.utc(scheduleRow.start_time);
  const endUtc = moment.utc(scheduleRow.end_time);
  if (!startUtc.isValid() || !endUtc.isValid()) {
    return null;
  }

  const dayStr = baseDay.format('YYYY-MM-DD');
  const start = moment.tz(dayStr, timezone).hour(startUtc.hour()).minute(startUtc.minute()).second(0).millisecond(0);

  if (scheduleRow.start_time === scheduleRow.end_time) {
    return { start, end: start.clone().add(1, 'day') };
  }

  let end = moment.tz(dayStr, timezone).hour(endUtc.hour()).minute(endUtc.minute()).second(0).millisecond(0);

  if (endUtc.hours() === 23 && endUtc.minutes() >= 59) {
    end = start.clone().add(1, 'day');
  } else if (endUtc.hours() === 0 && endUtc.minutes() === 0 && endUtc.isAfter(startUtc)) {
    end = start.clone().add(1, 'day');
  } else if (!end.isAfter(start)) {
    end.add(1, 'day');
  }

  return { start, end };
}

function isNowInWindow(now, window) {
  if (!window) {
    return false;
  }
  return now.isSameOrAfter(window.start) && now.isBefore(window.end);
}

export function isAmenityCurrentlyOpen(amenities_schedules, timezone) {
  if (!timezone || !Array.isArray(amenities_schedules) || amenities_schedules.length === 0) {
    return null;
  }

  const now = moment().tz(timezone);
  const todayDow = momentDayToApiDayOfWeek(now.day());
  const yesterdayDow = todayDow === 1 ? 7 : todayDow - 1;

  const todayWindow = buildScheduleWindow(
    findScheduleForDay(amenities_schedules, todayDow),
    now.clone().startOf('day'),
    timezone
  );
  if (isNowInWindow(now, todayWindow)) {
    return true;
  }

  const yesterdayWindow = buildScheduleWindow(
    findScheduleForDay(amenities_schedules, yesterdayDow),
    now.clone().subtract(1, 'day').startOf('day'),
    timezone
  );
  if (isNowInWindow(now, yesterdayWindow)) {
    return true;
  }

  return false;
}

export function getAmenityStatusLabel(isOpen, t) {
  if (isOpen === null) {
    return '—';
  }
  return isOpen ? t('Active') : t('Closed');
}
